// RigSentinel Context Engine — Phase RCE-1
//
// MIGRATION BANNER
// ----------------
// Non-blocking notification on app load: "RigSentinel has detected existing
// data that can be organized into Project Profiles."
//
// Actions:
//   * Start Migration → /migrate/projects
//   * Remind Me Later → marks state "snoozed"
//   * Dismiss          → marks state "dismissed" (never appears again)
//
// Once migration runs successfully the banner state is "completed" and the
// banner never appears again.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Database, X } from "lucide-react";
import {
  shouldShowMigrationBanner, setMigrationPromptState,
} from "@/lib/rce/migration";
import { on, RCE_EVENTS } from "@/lib/rce/events";

export default function MigrationBanner() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const refresh = () => setShow(shouldShowMigrationBanner());
    refresh();
    const off = on(RCE_EVENTS.MIGRATION_COMPLETED, refresh);
    return () => off();
  }, []);

  if (!show) return null;

  return (
    <div
      data-testid="rce-migration-banner"
      className="max-w-3xl mx-auto px-4 mt-2"
    >
      <div
        className="rounded-md border p-3 flex items-start gap-3"
        style={{
          background: "rgba(255,179,0,0.10)",
          borderColor: "rgba(255,179,0,0.35)",
        }}
      >
        <Database className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--rs-amber)" }} />
        <div className="flex-1 text-[12px] text-[var(--rs-text-1)] leading-snug">
          <div className="font-semibold text-[var(--rs-amber)] label-up text-[10px] mb-1">
            Project Profiles · RigSentinel Context Engine
          </div>
          RigSentinel has detected existing data that can be organized into
          <b> Project Profiles</b>. This lets every report inherit operator
          and client identity automatically.
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              data-testid="rce-migration-banner-start"
              onClick={() => navigate("/migrate/projects")}
              className="h-8 px-3 rounded text-[11px] label-up font-semibold"
              style={{ background: "var(--rs-amber)", color: "var(--rs-bg)" }}
            >
              Start Migration
            </button>
            <button
              data-testid="rce-migration-banner-snooze"
              onClick={() => { setMigrationPromptState("snoozed"); setShow(false); }}
              className="btn-ghost h-8 px-3 rounded text-[11px] label-up"
            >
              Remind Me Later
            </button>
            <button
              data-testid="rce-migration-banner-dismiss"
              onClick={() => { setMigrationPromptState("dismissed"); setShow(false); }}
              className="btn-ghost h-8 px-3 rounded text-[11px] label-up"
            >
              Dismiss
            </button>
          </div>
        </div>
        <button
          data-testid="rce-migration-banner-close"
          aria-label="Snooze migration banner"
          onClick={() => { setMigrationPromptState("snoozed"); setShow(false); }}
          className="btn-ghost h-7 w-7 rounded inline-flex items-center justify-center shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
